/**
 * FfmpegProcess
 *
 * Shared lifecycle wrapper for ffmpeg child processes used by screencast
 * encoders. Owns spawn, capped stderr collection, close/error/stdin-error
 * listeners, and stop-with-timeout-then-SIGKILL semantics.
 *
 * Encoders supply format-specific args and (optionally) subscribe to stdout
 * and to unexpected-exit notifications. See screencast-file-encoder.ts and
 * screencast-encoder.ts for callers.
 *
 * @module ffmpeg-process
 */

import { spawn, type ChildProcess } from 'child_process';
import { consoleLogger, type BrowserLogger } from './logger.js';

const STDERR_TAIL_CHARS = 2048;
const DEFAULT_STOP_TIMEOUT_MS = 30_000;
/** Bound for isFfmpegAvailable in case the binary hangs (shim/launcher edge cases). */
const FFMPEG_VERSION_TIMEOUT_MS = 5_000;
/** After SIGKILL, how long stop() waits for the OS to actually reap the process. */
const POST_KILL_REAP_MS = 1_000;

export interface FfmpegProcessOptions {
  /** Full ffmpeg argument list. Caller owns format-specific args. */
  args: string[];
  /** Log-prefix tag, e.g. "[ScreencastFileEncoder]" */
  logTag: string;
  /** Logger; defaults to consoleLogger */
  logger?: BrowserLogger;
  /** How long stop() waits for graceful exit before SIGKILL. Default 30_000ms. */
  stopTimeoutMs?: number;
  /** Receives raw ffmpeg stdout chunks. Subscribe when piping output (e.g. fMP4 atoms). */
  onStdout?: (chunk: Buffer) => void;
  /**
   * Called when ffmpeg exits with a non-zero code AND the caller did not call stop()/kill().
   * Caller-supplied; ScreencastFileEncoder omits this and logs only;
   * apps/api ScreencastEncoder forwards to its onError callback.
   */
  onUnexpectedExit?: (err: Error) => void;
  /**
   * @internal Test seam — override the spawn implementation. Production callers
   * never set this; tests pass a stub or a wrapper that swaps the binary.
   */
  spawnImpl?: typeof spawn;
}

/**
 * Owns one ffmpeg child process and its stdio lifecycle.
 *
 * Construct, then call `start()`. Write frames via `writeFrame()`. End with
 * either `stop()` (graceful: closes stdin, waits up to `stopTimeoutMs`, then
 * SIGKILLs and waits up to POST_KILL_REAP_MS for the OS to reap) or `kill()`
 * (immediate SIGKILL).
 *
 * Both `stop()` and `kill()` set the internal stopped flag, which suppresses
 * `onUnexpectedExit` for the close that follows. After stopped, `writeFrame`
 * is a no-op returning false.
 */
export class FfmpegProcess {
  private readonly args: string[];
  private readonly logTag: string;
  private readonly logger: BrowserLogger;
  private readonly stopTimeoutMs: number;
  private readonly onStdout?: (chunk: Buffer) => void;
  private readonly onUnexpectedExit?: (err: Error) => void;
  private readonly spawnImpl: typeof spawn;

  private proc: ChildProcess | null = null;
  private _started = false;
  private _stopped = false;
  /** Last STDERR_TAIL_CHARS of stderr (utf8 chars, mostly ASCII), used for diagnostics on exit. */
  private stderrTail = '';
  /** Pending stop()-timer; cleared by kill() or by the close handler. */
  private stopTimer: ReturnType<typeof setTimeout> | null = null;
  /** Guard: ensure onUnexpectedExit fires at most once even if 'error' and 'close' both arrive. */
  private _unexpectedFired = false;

  constructor(opts: FfmpegProcessOptions) {
    this.args = opts.args;
    this.logTag = opts.logTag;
    this.logger = opts.logger ?? consoleLogger;
    this.stopTimeoutMs = opts.stopTimeoutMs ?? DEFAULT_STOP_TIMEOUT_MS;
    this.onStdout = opts.onStdout;
    this.onUnexpectedExit = opts.onUnexpectedExit;
    this.spawnImpl = opts.spawnImpl ?? spawn;
  }

  get started(): boolean {
    return this._started;
  }

  get stopped(): boolean {
    return this._stopped;
  }

  /** True if stdin is currently writable (process up, not stopped). */
  get stdinWritable(): boolean {
    return !!this.proc?.stdin?.writable && !this._stopped;
  }

  /**
   * Spawn ffmpeg with the configured args. Idempotent — second call no-ops.
   * If spawn throws synchronously, _started remains false and onUnexpectedExit fires.
   */
  start(): void {
    if (this._started) return;

    let proc: ChildProcess;
    try {
      proc = this.spawnImpl('ffmpeg', this.args, {
        stdio: ['pipe', 'pipe', 'pipe'],
      });
    } catch (err) {
      const e = err instanceof Error ? err : new Error(String(err));
      this.logger.error(`${this.logTag} ffmpeg spawn threw synchronously`, {
        error: e.message,
        stack: e.stack,
      });
      this.fireUnexpectedExit(e);
      return;
    }

    this._started = true;
    this.proc = proc;

    proc.stderr?.on('data', (chunk: Buffer) => {
      this.stderrTail += chunk.toString();
      if (this.stderrTail.length > STDERR_TAIL_CHARS) {
        this.stderrTail = this.stderrTail.slice(-STDERR_TAIL_CHARS);
      }
    });

    if (this.onStdout) {
      proc.stdout?.on('data', this.onStdout);
    }

    proc.on('close', (code) => {
      const tail = this.stderrTail.slice(-STDERR_TAIL_CHARS);
      if (code !== 0 && code !== null && !this._stopped) {
        const err = new Error(`ffmpeg exited with code ${code}: ${tail}`);
        this.logger.error(`${this.logTag} ffmpeg exited unexpectedly`, {
          code,
          stderr: tail,
        });
        this.fireUnexpectedExit(err);
      } else {
        this.logger.info(`${this.logTag} ffmpeg exited`, { code: code ?? undefined });
      }
      this.proc = null;
    });

    proc.on('error', (err) => {
      this.logger.error(`${this.logTag} ffmpeg spawn error`, {
        error: err.message,
        stack: err.stack,
      });
      if (!this._stopped) {
        this.fireUnexpectedExit(err);
      }
      this.proc = null;
    });

    proc.stdin?.on('error', (err) => {
      if (!this._stopped) {
        this.logger.warn(`${this.logTag} stdin error`, { error: err.message });
      }
    });
  }

  /**
   * Write a frame (or any chunk) to ffmpeg's stdin.
   *
   * Returns the underlying Node `Writable.write()` boolean — `true` when the
   * stream accepted without backpressure, `false` when the chunk was buffered
   * and the caller should pause (e.g. await `'drain'`) before writing more.
   * Returns `false` immediately if the process is not in a writable state.
   * Optional callback fires after the write completes (Node stream callback).
   */
  writeFrame(buf: Buffer, cb?: (err?: Error | null) => void): boolean {
    const proc = this.proc;
    if (!proc?.stdin?.writable || this._stopped) {
      // Always invoke the callback asynchronously to keep callers' async assumptions consistent.
      if (cb) queueMicrotask(() => cb(null));
      return false;
    }
    return proc.stdin.write(buf, (err) => cb?.(err ?? null));
  }

  /**
   * Graceful stop: marks stopped, closes stdin to signal EOF, waits up to
   * `stopTimeoutMs` for ffmpeg to exit, then SIGKILLs and waits briefly for
   * the OS to reap. Idempotent.
   *
   * Concurrent callers share the same close — both await the same close event.
   */
  async stop(): Promise<void> {
    if (this._stopped) {
      // If a stop is already in flight, wait for the proc to actually be gone.
      const proc = this.proc;
      if (proc) await new Promise<void>((r) => proc.once('close', () => r()));
      return;
    }
    this._stopped = true;

    const proc = this.proc;
    if (!proc) return;

    // If the process already exited (race), the existing close handler already ran.
    if (proc.exitCode !== null || proc.signalCode !== null) return;

    return new Promise<void>((resolve) => {
      const onClose = () => {
        if (this.stopTimer) {
          clearTimeout(this.stopTimer);
          this.stopTimer = null;
        }
        resolve();
      };
      proc.once('close', onClose);

      // Signal EOF; ffmpeg flushes pending output and exits.
      proc.stdin?.end();

      this.stopTimer = setTimeout(() => {
        this.logger.warn(`${this.logTag} ffmpeg did not exit in time, killing`, {
          timeoutMs: this.stopTimeoutMs,
        });
        proc.kill('SIGKILL');
        // Wait briefly for the OS to actually reap so callers don't treat
        // output as final while ffmpeg is still flushing/exiting.
        const reapTimer = setTimeout(() => {
          proc.removeListener('close', onClose);
          this.stopTimer = null;
          resolve();
        }, POST_KILL_REAP_MS);
        // If close arrives first, cancel the reap fallback.
        proc.once('close', () => clearTimeout(reapTimer));
      }, this.stopTimeoutMs);
    });
  }

  /** Immediate SIGKILL. Idempotent. Cancels any in-flight stop() timer. */
  kill(): void {
    this._stopped = true;
    if (this.stopTimer) {
      clearTimeout(this.stopTimer);
      this.stopTimer = null;
    }
    if (this.proc) {
      this.proc.kill('SIGKILL');
      this.proc = null;
    }
  }

  /** Fire onUnexpectedExit at most once, regardless of which event triggered it. */
  private fireUnexpectedExit(err: Error): void {
    if (this._unexpectedFired) return;
    this._unexpectedFired = true;
    this.onUnexpectedExit?.(err);
  }
}

/**
 * Check whether the `ffmpeg` binary is available on PATH.
 * Used to decide whether to enable video encoding or fall back to base64 frames.
 *
 * Times out after FFMPEG_VERSION_TIMEOUT_MS in case the binary hangs.
 */
export async function isFfmpegAvailable(): Promise<boolean> {
  return new Promise((resolve) => {
    let settled = false;
    const settle = (v: boolean) => {
      if (settled) return;
      settled = true;
      resolve(v);
    };
    let proc: ChildProcess;
    try {
      proc = spawn('ffmpeg', ['-version'], { stdio: 'ignore' });
    } catch {
      settle(false);
      return;
    }
    const timer = setTimeout(() => {
      try {
        proc.kill('SIGKILL');
      } catch {
        /* already gone */
      }
      settle(false);
    }, FFMPEG_VERSION_TIMEOUT_MS);
    proc.on('close', (code) => {
      clearTimeout(timer);
      settle(code === 0);
    });
    proc.on('error', () => {
      clearTimeout(timer);
      settle(false);
    });
  });
}
